import { addHours } from 'date-fns';
import { ShiftTypesEnum } from '../../../js/api';
import { parseTime } from './time';

export interface ShiftSettings {
  shift_length: number;
  shift_start_day: string;
  shift_start_night: string;
  shift_start_24h: string;
}

export const DEFAULT_SHIFT_SETTINGS: ShiftSettings = {
  shift_length: 12,
  shift_start_day: '07:00:00',
  shift_start_night: '19:00:00',
  shift_start_24h: '07:00:00',
};

const getStartTimeStringForType = (
  shiftType: ShiftTypesEnum,
  shiftSettings: ShiftSettings
): string => {
  switch (shiftType) {
    case ShiftTypesEnum.NIGHT:
      return shiftSettings.shift_start_night;
    case ShiftTypesEnum._24H:
      return shiftSettings.shift_start_24h;
    case ShiftTypesEnum.DAY:
    default:
      return shiftSettings.shift_start_day;
  }
};

const getShiftLengthForType = (shiftType: ShiftTypesEnum, shiftSettings: ShiftSettings): number =>
  shiftType === ShiftTypesEnum._24H ? 24 : shiftSettings.shift_length;

/**
 * Compute start/end `Date` for a shift of the given type on the given date,
 * using the facility's shift settings. 24H shifts always use 24h length.
 */
export const calculateShiftTimes = (
  date: Date,
  shiftType: ShiftTypesEnum,
  shiftSettings: ShiftSettings
): { startTime: Date; endTime: Date } => {
  const { hours, minutes } = parseTime(getStartTimeStringForType(shiftType, shiftSettings));
  const startTime = new Date(date);
  startTime.setHours(hours, minutes, 0, 0);
  const endTime = addHours(startTime, getShiftLengthForType(shiftType, shiftSettings));
  return { startTime, endTime };
};

/**
 * Compute default start/end time-of-day strings (HH:mm) for a shift of the
 * given type, using the facility's shift settings. End time wraps at 24h —
 * date overflow for NIGHT / 24H shifts must be handled by the caller.
 */
export const getShiftTypeDefaults = (
  shiftType: ShiftTypesEnum,
  shiftSettings: ShiftSettings
): { startTime: string; endTime: string } => {
  const startStr = getStartTimeStringForType(shiftType, shiftSettings);
  const { hours, minutes } = parseTime(startStr);
  const lengthHours = getShiftLengthForType(shiftType, shiftSettings);
  const totalMinutes = hours * 60 + minutes + lengthHours * 60;
  const endH = Math.floor(totalMinutes / 60) % 24;
  const endM = totalMinutes % 60;
  return {
    startTime: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
    endTime: `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`,
  };
};
